// The preview-then-confirm modal shared by the grant and revoke flows
// (ADR-0047: a preview is a forecast, never a gate — every item is reported,
// rejected ones included, and confirming runs the same items for real).
import {
  Badge,
  Button,
  Grid,
  Modal,
  Table,
  Tooltip,
} from "@intility/bifrost-react";
import type { BulkRolesResponse } from "~/api/grants";
import type { CatalogCompany } from "~/api/types";
import { objectLabel, ROLE_LABEL, subjectLabel } from "./labels";

const OUTCOME_STATE: Record<
  string,
  "attn" | "success" | "warning" | "chill" | "neutral"
> = {
  granted: "success",
  revoked: "chill",
  unchanged: "neutral",
  superseded: "warning",
  rejected: "attn",
};

function outcomeCounts(result: BulkRolesResponse): string {
  const parts: string[] = [];
  if (result.counts.granted > 0) parts.push(`${result.counts.granted} granted`);
  if (result.counts.revoked > 0) parts.push(`${result.counts.revoked} revoked`);
  if (result.counts.unchanged > 0)
    parts.push(`${result.counts.unchanged} unchanged`);
  if (result.counts.superseded > 0)
    parts.push(`${result.counts.superseded} superseded`);
  if (result.counts.rejected > 0)
    parts.push(`${result.counts.rejected} rejected`);
  return parts.join(", ");
}

// An access change is never silent (ADR-0038): name the weaker grants an
// absorbed item removed without the caller asking.
function supersededText(
  superseded: BulkRolesResponse["items"][number]["superseded"],
): string | null {
  if (!superseded || superseded.length === 0) return null;
  return `Also removes: ${superseded
    .map((s) => `${ROLE_LABEL[s.role] ?? s.role} on ${s.object}`)
    .join(", ")}`;
}

export function BulkOutcomeModal({
  mode,
  result,
  busy,
  companies,
  onConfirm,
  onClose,
}: {
  mode: "grant" | "revoke";
  result: BulkRolesResponse;
  busy: boolean;
  companies: CatalogCompany[] | undefined;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const count = result.counts.total;

  return (
    <Modal
      isOpen
      onRequestClose={busy ? undefined : onClose}
      header={
        mode === "grant"
          ? `Grant ${count} role assignments`
          : `Revoke ${count} role assignments`
      }
      width={720}
    >
      <Grid gap={16}>
        <p className="bf-p" style={{ margin: 0 }}>
          {mode === "grant" ? (
            <>
              Preview outcome: <strong>{outcomeCounts(result)}</strong>. Confirm
              to apply.
            </>
          ) : (
            <>
              Preview outcome: <strong>{outcomeCounts(result)}</strong>. Confirm
              to revoke.
            </>
          )}
        </p>

        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Subject</Table.HeaderCell>
              <Table.HeaderCell>Role</Table.HeaderCell>
              <Table.HeaderCell>Object</Table.HeaderCell>
              <Table.HeaderCell>Outcome</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {result.items.map((item) => (
              <Table.Row key={item.index}>
                <Table.Cell>
                  <Tooltip content={item.user}>
                    <span>{subjectLabel(item.user)}</span>
                  </Tooltip>
                </Table.Cell>
                <Table.Cell>
                  {ROLE_LABEL[item.relation] ?? item.relation}
                </Table.Cell>
                <Table.Cell>
                  <Tooltip content={item.object}>
                    <span>{objectLabel(item.object, companies)}</span>
                  </Tooltip>
                </Table.Cell>
                <Table.Cell>
                  <Grid gap={4}>
                    <Badge state={OUTCOME_STATE[item.outcome] ?? "neutral"}>
                      {item.outcome}
                    </Badge>
                    {item.outcome === "rejected" && item.message && (
                      <span className="bf-small bfc-base-2">
                        {item.code ? `${item.code}: ` : ""}
                        {item.message}
                      </span>
                    )}
                    {supersededText(item.superseded) && (
                      <span className="bf-small bfc-base-2">
                        {supersededText(item.superseded)}
                      </span>
                    )}
                  </Grid>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button
            variant="flat"
            onClick={busy ? undefined : onClose}
            state={busy ? "inactive" : undefined}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            state={busy ? "inactive" : mode === "revoke" ? "alert" : undefined}
            onClick={busy ? undefined : onConfirm}
          >
            {busy
              ? "Working…"
              : mode === "grant"
                ? `Grant ${count} role ${count === 1 ? "assignment" : "assignments"}`
                : `Revoke ${count} role ${count === 1 ? "assignment" : "assignments"}`}
          </Button>
        </div>
      </Grid>
    </Modal>
  );
}
